import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { IconImage } from '../../../../base/icons/svg';
import { Video } from '../../../../base/media/components/index';
import { equals } from '../../../../base/redux/functions';
import { updateSettings } from '../../../../base/settings/actions';
import { withPixelLineHeight } from '../../../../base/styles/functions.web';
import Checkbox from '../../../../base/ui/components/web/Checkbox';
import ContextMenu from '../../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../../base/ui/components/web/ContextMenuItemGroup';
import { checkBlurSupport, checkVirtualBackgroundEnabled } from '../../../../virtual-background/functions';
import { createLocalVideoTracks } from '../../../functions.web';
import { BUTTON_TYPES } from "../../../../base/ui/constants.any";
import Button from "../../../../base/ui/components/web/Button";
import Icon from "../../../../base/icons/components/Icon";
import { openDialog } from "../../../../base/dialog/actions";
import CompassVirtualBackgroundDialog from "../../../../virtual-background/components/web/CompassVirtualBackgroundDialog";
const useStyles = makeStyles()(theme => {
    return {
        container: {
            padding: '16px 0',
            maxHeight: 'calc(100dvh - 100px)',
            overflow: 'auto',
            margin: 0,
            minWidth: '272px',
            marginBottom: '12px',
            position: 'relative',
            right: 'auto'
        },
        previewEntry: {
            cursor: 'pointer',
            height: '134px',
            width: '240px',
            position: 'relative',
            margin: '0 16px',
            marginBottom: '4px',
            borderRadius: theme.shape.borderRadius,
            boxSizing: 'border-box',
            overflow: 'hidden',
        },
        selectedEntry: {
            border: `2px solid ${theme.palette.action01Hover}`
        },
        previewVideo: {
            height: '100%',
            width: '100%',
            objectFit: 'cover'
        },
        error: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'absolute',
            padding: '17px 12px',
            backgroundColor: '#2C2C2C',
            fontSize: '12px',
            borderRadius: theme.shape.borderRadius,
            letterSpacing: '-0.15px',
            textAlign: 'center',
            verticalAlign: 'middle',
            color: '#FFFFFFBF',
            boxSizing: 'border-box',
            cursor: 'default',
        },
        labelContainer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            maxWidth: '100%',
            zIndex: 2,
            padding: theme.spacing(2)
        },
        label: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '4px',
            padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            color: theme.palette.text01,
            ...withPixelLineHeight(theme.typography.labelBold),
            width: 'fit-content',
            maxwidth: `calc(100% - ${theme.spacing(2)} - ${theme.spacing(2)})`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        checkboxContainer: {
            padding: '0 12px'
        },
        checkbox: {
            paddingLeft: '4px',
            paddingRight: '0px',
            width: '100%',
            '&:hover': {
                borderRadius: '0 !important',
                '.checkbox-text': {
                    color: 'rgba(255, 255, 255, 1)',
                },
            }
        },
        buttonContainer: {
            padding: '0 16px',
            marginTop: '8px'
        },
    };
});
const stopPropagation = (e) => {
    e.stopPropagation();
};
const VideoSettingsContent = ({ changeFlip, currentCameraDeviceId, disableLocalVideoFlip, localFlipX, selectBackground, setVideoInputDevice, toggleVideoSettings, videoDeviceIds, visibleVirtualBackground }) => {
    const _componentWasUnmounted = useRef(false);
    const [trackData, setTrackData] = useState(new Array(videoDeviceIds.length).fill({
        jitsiTrack: null
    }));
    const { t } = useTranslation();
    const videoDevicesRef = useRef(videoDeviceIds);
    const trackDataRef = useRef(trackData);
    const { classes, cx } = useStyles();
    /**
     * Toggles local video flip state.
     *
     * @returns {void}
     */
    const _onToggleFlip = useCallback(() => {
        changeFlip(!localFlipX);
    }, [localFlipX, changeFlip]);
    /**
     * Destroys all the tracks from trackData object.
     *
     * @param {Object[]} tracks - An array of tracks that are to be disposed.
     * @returns {Promise<void>}
     */
    const _disposeTracks = (tracks) => {
        tracks.forEach(({ jitsiTrack }) => {
            jitsiTrack?.dispose();
        });
    };
    /**
     * Creates and updates the track data.
     *
     * @returns {void}
     */
    const _setTracks = async () => {
        _disposeTracks(trackData);
        const newTrackData = await createLocalVideoTracks(videoDeviceIds, 5000);
        // In case the component gets unmounted before the tracks are created
        // avoid a leak by not setting the state
        if (_componentWasUnmounted.current) {
            _disposeTracks(newTrackData);
        }
        else {
            setTrackData(newTrackData);
            trackDataRef.current = newTrackData;
        }
    };
    /**
     * Returns the click handler used when selecting the video preview.
     *
     * @param {string} deviceId - The id of the camera device.
     * @returns {Function}
     */
    const _onEntryClick = (deviceId) => () => {
        setVideoInputDevice(deviceId);
        toggleVideoSettings();
    };
    const cleanLabel = (label) => {
        if (label === undefined) {
            return "";
        }
        return label.replace(/\([a-zA-Z0-9]{1,10}:[a-zA-Z0-9]{1,10}\)/, '').trim();
    };
    /**
     * Renders a preview entry.
     *
     * @param {Object} data - The track data.
     * @param {number} index - The index of the entry.
     * @returns {React$Node}
     */
    // eslint-disable-next-line react/no-multi-comp
    const _renderPreviewEntry = (data, index) => {
        const { error, jitsiTrack, deviceId } = data;
        const key = `vp-${index}`;
        const isSelected = data.isSelected;
        const tabIndex = '0';
        if (error) {
            let className = classes.previewEntry;
            if (isSelected) {
                className = cx(classes.previewEntry, classes.selectedEntry);
            }
            return (React.createElement("div", { className: className, key: key, tabIndex: -1 },
                React.createElement("div", { className: classes.error }, t(error))));
        }
        const previewProps = {
            className: classes.previewEntry,
            key,
            tabIndex
        };
        const label = cleanLabel(jitsiTrack?.getTrackLabel());
        if (isSelected) {
            previewProps['aria-checked'] = true;
            previewProps.className = cx(classes.previewEntry, classes.selectedEntry);
        }
        else {
            previewProps.onClick = _onEntryClick(deviceId);
            previewProps.onKeyPress = (e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    previewProps.onClick();
                }
            };
        }
        return (React.createElement("div", { ...previewProps, role: 'radio' },
            React.createElement("div", { className: classes.labelContainer }, label && React.createElement("div", { className: classes.label },
                React.createElement("span", null, label))),
            React.createElement(Video, { className: cx(classes.previewVideo, localFlipX && 'flipVideoX'), playsinline: true, videoTrack: { jitsiTrack } })));
    };
    const withSelection = (trackData, currentCameraDeviceId) => {
        const firstNonWorkingIndex = !currentCameraDeviceId
            ? trackData.findIndex((data) => data.error === 'deviceSelection.previewUnavailable')
            : -1;
        return trackData.map((data, i) => ({
            ...data,
            isSelected: (currentCameraDeviceId && data.deviceId === currentCameraDeviceId) ||
                (!currentCameraDeviceId && i === firstNonWorkingIndex),
        }));
    };
    useEffect(() => {
        _setTracks();
        return () => {
            _componentWasUnmounted.current = true;
            _disposeTracks(trackDataRef.current);
        };
    }, []);
    useEffect(() => {
        if (!equals(videoDeviceIds, videoDevicesRef.current)) {
            _setTracks();
            videoDevicesRef.current = videoDeviceIds;
        }
    }, [videoDeviceIds]);
    return (React.createElement(ContextMenu, { activateFocusTrap: true, "aria-labelledby": 'video-settings-button', className: classes.container, hidden: false, id: 'video-settings-dialog', role: 'radiogroup', tabIndex: -1 },
        React.createElement(ContextMenuItemGroup, null, withSelection(trackData, currentCameraDeviceId).map((data, i) => _renderPreviewEntry(data, i))),
        React.createElement(ContextMenuItemGroup, null,
            !disableLocalVideoFlip && (React.createElement("div", { className: classes.checkboxContainer, onClick: stopPropagation },
                React.createElement(Checkbox, { className: classes.checkbox, classNameText: 'checkbox-text', checked: localFlipX, label: t('videothumbnail.mirrorVideo'), onChange: _onToggleFlip }))),
            visibleVirtualBackground &&
                React.createElement("div", { className: classes.buttonContainer },
                    React.createElement(Button, { accessibilityLabel: t('virtualBackground.title'), fullWidth: true, labelKey: 'virtualBackground.title', onClick: selectBackground, type: BUTTON_TYPES.PRIMARY, customIcon: React.createElement(Icon, { size: 20, src: IconImage }) })))));
};
const mapStateToProps = (state) => {
    const { disableLocalVideoFlip } = state['features/base/config'];
    const { localFlipX } = state['features/base/settings'];
    return {
        disableLocalVideoFlip,
        localFlipX: Boolean(localFlipX),
        visibleVirtualBackground: checkBlurSupport()
            && checkVirtualBackgroundEnabled(state)
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        selectBackground: () => dispatch(openDialog(CompassVirtualBackgroundDialog)),
        changeFlip: (flip) => {
            dispatch(updateSettings({
                localFlipX: flip
            }));
        }
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(VideoSettingsContent);
